import type {Delimiter, Root} from 'mdast'

import {getLineRange, getLineRanges} from '../source-utils'
import {getNodesInsideRange} from '../syntax-tree/node-range-utils'
import {isPositionedNode} from '../syntax-tree/positioned-node'
import {trimmedDelimiterPosition} from './delimiter-utils'
import {prefixTokenTypes} from './extensions/contain-delimiters'
import {isTasklistPrefix} from './list-utils'

/**
 * Utility class that provides an abstraction around a line of Markdown source code, augmented with data from the tree.
 * Useful for finding the boundaries of lines and managing their 'prefixes' (Markdown syntax that repeats at the start
 * lines to form a block).
 */
export class MarkdownLine {
  readonly start: number
  readonly end: number

  #tree: Root
  #source: string

  private constructor(tree: Root, source: string, start: number, end: number) {
    this.#tree = tree
    this.#source = source
    this.start = start
    this.end = end
  }

  /** Get the array of all prefix delimiter nodes at the beginning of this line. */
  get prefixes() {
    return (this.#_prefixes ??= getNodesInsideRange(this.#tree, this.start, this.end).filter(
      (node): node is Delimiter => node.type === 'delimiter' && prefixTokenTypes.has(node.tokenType),
    ))
  }
  #_prefixes: readonly Delimiter[] | null = null

  /** Get the last prefix before the content. */
  get lastPrefix() {
    return this.prefixes.at(-1)
  }

  /** Document relative offset of the start of the content of this line (excluding prefixes). */
  get contentStart() {
    return this.lastPrefix?.position?.end.offset ?? this.start
  }

  /** Get the text of the line, excluding prefixes. */
  get content() {
    return this.#source.slice(this.contentStart, this.end)
  }

  get isBlank() {
    // empty tasklist items get parsed as a regular list item with '[ ]' content, so we have to add a special case
    return isTasklistPrefix(this.content) || this.content.trim().length === 0
  }

  /** Get the previous line. */
  previous() {
    return this.start === 0 ? null : MarkdownLine.atOffset(this.#tree, this.#source, this.start - 1)
  }

  /** Get the next line. */
  next() {
    return this.end >= this.#source.length ? null : MarkdownLine.atOffset(this.#tree, this.#source, this.end + 1)
  }

  /** Check if the line overlaps the given range. */
  overlaps(from: number, to: number) {
    return (
      (from >= this.start && from < this.end) ||
      (to > this.start && to <= this.end) ||
      (from <= this.start && to >= this.end)
    )
  }

  /**
   * Get the prefixes before the given position, ignoring trailing whitespace in prefixes.
   * @param offset A **document-relative** offset inside the line.
   */
  prefixesBefore(offset: number) {
    return this.prefixes.filter(node => isPositionedNode(node) && trimmedDelimiterPosition(node).endOffset <= offset)
  }

  /**
   * Check if the offset is after all prefixes on the line, ignoring trailing whitespace in the last prefix.
   * For example, `> |hello`, `>| hello`, and `>  hello|` would all return true for an offset at `|`.
   * @param offset A **document-relative** offset.
   */
  isOffsetAfterPrefixes(offset: number) {
    return this.lastPrefix === this.prefixesBefore(offset).at(-1)
  }

  /**
   * Construct a `MarkdownLine` for the line containing the given offset.
   * @param tree The mdast tree for the entire document.
   * @param source The Markdown source code of the entire document.
   * @param offset Document-relative index of any character which is contained in the line.
   */
  static atOffset(tree: Root, source: string, offset: number): MarkdownLine | null {
    const range = getLineRange(source, offset)
    return range && new MarkdownLine(tree, source, range.start, range.end)
  }

  /**
   * Construct an array of all of the `MarkdownLine`s that overlap the given range.
   * @param tree The mdast tree for the entire document.
   * @param source The Markdown source code of the entire document.
   * @param from Document-relative index of the start of the range.
   * @param to Document-relative index of the end of the range.
   */
  static linesAtRange(tree: Root, source: string, from: number, to: number): MarkdownLine[] {
    const ranges = getLineRanges(source, from, to)
    return ranges.map(({start, end}) => new MarkdownLine(tree, source, start, end))
  }
}
